import React from 'react';

import { Text } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import type { TranslationKey } from '@change-corgi/core/i18n';
import { Translate } from '@change-corgi/core/react/i18n';

/**
 * Wrapper component for sharing tools cards that provides common structure.
 * Includes the header with customizable title and description, accepts children for the card body.
 *
 * This component can be used across different card implementations in the SharingTools section.
 *
 * ```tsx
 * <SharingToolsCardWrapperVariant1
 *   title="Recommended Groups"
 *   description="Get 100+ new signatures by sharing in online groups"
 * >
 *   <YourCardContent />
 * </SharingToolsCardWrapperVariant1>
 * ```
 */
export const SharingToolsCardWrapperVariant1: React.FC<SharingToolsCardWrapperProps> = ({
	children,
	title,
	description,
	className = '',
	primaryActionOnClick,
}) => {
	return (
		<div
			data-testid="sharing-tools-card-wrapper-variant1"
			role="button"
			tabIndex={0}
			onClick={primaryActionOnClick}
			onKeyDown={(e) => {
				if (e.key === 'Enter' || e.key === ' ') primaryActionOnClick();
			}}
			className={cn(
				'flex max-w-[230px] flex-col items-start gap-4 rounded-xl border-[3px] border-transparent bg-fill-information-weaker px-6 py-8 text-typography-strong transition-colors active:border-stroke-info-strong active:bg-fill-information-weaker hover:border-stroke-info-strong hover:bg-fill-information-weaker md:p-6',
				className,
			)}
			aria-label="Sharing tools - Social content"
			data-qa="sharing-tools-card-wrapper-variant-1"
		>
			<header className="flex flex-col gap-1 self-stretch">
				{title ? (
					<Text variant="heading" size="xx-small" className="text-left text-typography-strong">
						<Translate value={title} />
					</Text>
				) : null}
				{description ? (
					<Text
						as="p"
						variant="body"
						size="small"
						className="overflow-hidden text-ellipsis text-left text-typography-strong"
					>
						<Translate value={description} />
					</Text>
				) : null}
			</header>
			<div className="flex min-h-48 items-center justify-center self-stretch">{children}</div>
		</div>
	);
};

type SharingToolsCardWrapperProps = Readonly<{
	children: React.ReactNode;
	title?: TranslationKey;
	description?: TranslationKey;
	className?: string;
	primaryActionOnClick: () => void;
}>;
